import { Button } from '@arckit/ui'
import Layout from 'components/Layout'
import { Input, Select, TextArea } from 'components/forms/Inputs'
import { Row, Col, Section } from 'components/grid'
import { H2, H3 } from 'components/typography/Headings'
import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { states } from '../constants'
import { P } from 'components/typography/Paragraph'
import AnimatedCheckmark from 'components/global/AnimatedCheckmark'
import Stepper from 'components/global/Stepper'
import { wait } from 'utils/wait'
import { navigate } from 'gatsby'
import { IPageProps } from 'common-types'
import { twMerge } from 'tailwind-merge'
import { isEmpty } from 'utils/isObjectEmpty'
import { supabase } from 'clients/supabase'
import axios from 'axios'
import { format } from 'date-fns'
import addLeadToSalesforceCampaign from 'components/forms/addLeadToSalesforceCampaign'
import { location } from '@sideway/pinpoint'

interface ConnectPageState {
	pageName: string
	email?: string
	downLoadAfterSubmit?: string
}

interface FormState {
	first_name: string
	last_name: string
	email: string
	phone: string
	position: string
	city: string
	state: string
	zip_code: string
	district: string
	products: string[]
	comments: string
}

export const productOptions = [
	{
		name: 'Core Curriculum',
		description: 'Knowledge-building curriculum aligned to the science of reading.'
	},
	{
		name: 'Supplemental',
		description: 'Pair with any core program to accelerate learning.'
	},
	{
		name: 'Intervention',
		description: 'Additional supports to help students become better readers.'
	},
	{
		name: 'Assessment',
		description: 'A game-changing formative assessment for reading.'
	},
	{
		name: 'Summer',
		description: 'Extend learning into summer with content-rich resources.'
	},
	{
		name: 'Pilot Programs',
		description: 'See how ARC Core can transform your classroom.'
	},
	{
		name: 'Libraries',
		description: 'Perfectly curated knowledge-building classroom libraries.'
	},
	{
		name: 'Tutoring',
		description: 'High-quality resources to support students beyond the classroom.'
	},
	{
		name: 'Other',
		description: 'Professional development, leadership learning, and more.'
	}
]

export default function ConnectPage({ location: { state } }: IPageProps<ConnectPageState>) {
	const email = state?.email || ''
	const pageName = state?.pageName || 'Connect'

	const defaultState = {
		first_name: '',
		last_name: '',
		email: email,
		phone: '',
		position: '',
		city: '',
		state: '',
		zip_code: '',
		district: '',
		products: [],
		comments: ''
	}
	const [formState, setFormState] = useState<FormState>(defaultState)
	const [step, setStep] = useState(0)
	const [loading, setLoading] = useState(false)

	const methods = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' })

	function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
		const { name, value } = event.target
		setFormState((prevState) => ({
			...prevState,
			[name]: value
		}))
	}

	function toggleString(arr: FormState['products'], value: string) {
		if (arr.includes(value)) {
			return arr.filter((item) => item !== value)
		} else {
			return [...arr, value]
		}
	}

	function handleSelectProduct(value: string) {
		setFormState((prevState) => ({
			...prevState,
			products: toggleString(prevState.products, value)
		}))
	}

	async function submitForm() {
		setLoading(true)
		await supabase.from('form_submissions').insert({
			first_name: formState.first_name,
			last_name: formState.last_name,
			email: formState.email,
			phone: formState.phone,
			title: formState.position,
			city: formState.city,
			state: formState.state,
			zip_code: formState.zip_code,
			school_district: formState.district,
			comments: formState.comments,
			page: pageName,
			products: formState.products
		})
		axios.post(`/api/email/new-connect-lead`, {
			contact: {
				firstName: formState.first_name,
				lastName: formState.last_name,
				email: formState.email,
				phone: formState.phone,
				position: formState.position,
				city: formState.city,
				state: formState.state,
				zipCode: formState.zip_code,
				district: formState.district,
				comments: formState.comments,
				pageName: pageName,
				products: formState.products,
				submitDate: format(new Date(), 'MMM do yyyy, h:mm:ss a')
			}
		})
		axios.post(`/api/salesforce/capture-lead`, {
			contact: {
				firstName: formState.first_name,
				lastName: formState.last_name,
				email: formState.email,
				phone: formState.phone,
				position: formState.position,
				city: formState.city,
				state: formState.state,
				zipCode: formState.zip_code,
				district: formState.district,
				comments: formState.comments,
				pageName: pageName,
				products: formState.products,
				submitDate: format(new Date(), 'MMM do yyyy, h:mm:ss a')
			}
		})

		setLoading(false)
		setStep(step + 1)
		await wait(1500)
		navigate('/thank-you', {
			state: {
				first_name: formState.first_name,
				zip_code: formState.zip_code,
				params: new URLSearchParams({ ...formState, products: JSON.stringify(formState.products) }).toString(),
				downLoadAfterSubmit: state?.downLoadAfterSubmit
			}
		})
	}

	function goToThankYou() {
		navigate('/thank-you', {
			state: {
				first_name: 'Tester',
				zip_code: 19460,
				downLoadAfterSubmit: state?.downLoadAfterSubmit
			}
		})
	}

	console.log("state: ", state);

	const slideTransition = {
		duration: 0.5, // in seconds
		ease: 'anticipate'
	}

	const slideHorizontalVariants = {
		initial: { x: '100%', opacity: 0, transition: slideTransition },
		enter: { x: '0%', opacity: 1, transition: slideTransition },
		exit: { x: '-100%', opacity: 0, transition: slideTransition }
	}

	const slideVerticalVariants = {
		initial: { y: '100%', opacity: 0, transition: slideTransition },
		enter: { y: '0%', opacity: 1, transition: slideTransition },
		exit: { y: '-100%', opacity: 0, transition: slideTransition }
	}

	function step1Disabled() {
		if (!isEmpty(methods.errors)) return true
		if (!formState.first_name || !formState.last_name || !formState.email || !formState.zip_code) return true
		else return false
	}

	function step2Disabled() {
		if (!isEmpty(methods.errors)) return true
		if (!formState.district) return true
		else return false
	}

	const steps = [
		<div className="min-h-[300px]">
			<div className="mb-8 grid grid-cols-2 gap-x-4 gap-y-8">
				<div className="col-span-1">
					<Input
						validation={methods.register(inputValidations.first_name)}
						value={formState.first_name}
						type="text"
						label="First Name"
						id="first_name"
						required
						onChange={handleChange}
					/>
				</div>
				<div className="col-span-1">
					<Input
						validation={methods.register(inputValidations.last_name)}
						value={formState.last_name}
						type="text"
						label="Last Name"
						id="last_name"
						required
						onChange={handleChange}
					/>
				</div>
				<div className="col-span-1">
					<Input validation={methods.register(inputValidations.email)} value={formState.email} type="email" label="Email" id="email" required onChange={handleChange} />
				</div>
				<div className="col-span-1">
					<Input
						value={formState.zip_code}
						validation={methods.register(inputValidations.zip_code)}
						type="text"
						label="Zip Code"
						id="zip_code"
						maxLength={5}
						onChange={handleChange}
						required
					/>
				</div>
			</div>
			<div className="flex justify-end">
				<Button disabled={step1Disabled()} type="button" onClick={() => setStep(step + 1)} size="lg">
					Continue
				</Button>
			</div>
		</div>,
		<div className="min-h-[300px]">
			<div className="mb-8 grid grid-cols-6 gap-x-4 gap-y-8 md:grid-cols-3">
				<div className="col-span-6 md:col-span-1">
					<Input value={formState.phone} type="text" label="Phone" id="phone" onChange={handleChange} />
				</div>
				<div className="col-span-3 md:col-span-1">
					<Input value={formState.city} type="text" label="City" id="city" onChange={handleChange} />
				</div>
				<div className="col-span-3 md:col-span-1">
					<Select value={formState.state} label="State" id="state" items={states} onChange={handleChange} />
				</div>
			</div>
			<div className="mb-8 grid grid-cols-2 gap-x-4 gap-y-8">
				<div className="col-span-1">
					<Input
						required
						validation={methods.register(inputValidations.district)}
						value={formState.district}
						type="text"
						label="District"
						id="district"
						onChange={handleChange}
					/>
				</div>
				<div className="col-span-1">
					<Input value={formState.position} type="text" label="Position" id="position" onChange={handleChange} />
				</div>
			</div>
			<div className="flex justify-between">
				<Button type="button" variant="light" onClick={() => setStep(step - 1)} size="lg">
					Back
				</Button>
				<Button disabled={step2Disabled()} type="button" onClick={() => setStep(step + 1)} size="lg">
					Continue
				</Button>
			</div>
		</div>,
		<div className="min-h-[300px]">
			<div className="mb-8 grid grid-cols-6 justify-items-center gap-x-4 gap-y-6 lg:grid-cols-3">
				{productOptions.map((p) => {
					return (
						<div key={`product-select-button-${p.name}`} className="col-span-3 lg:col-span-1">
							<button
								type="button"
								onClick={() => handleSelectProduct(p.name)}
								className={twMerge(
									'relative flex h-full flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-3 text-center hover:bg-ab-100/10',
									formState.products.includes(p.name) && 'border-ab-100 bg-ab-100/10'
								)}
							>
								<span className="font-semibold capitalize">{p.name}</span>
								<P size="xs" className="block text-sm">
									{p.description}
								</P>
								{formState.products.includes(p.name) && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="currentColor"
										className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-white text-ab-100"
									>
										<path
											fillRule="evenodd"
											d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
											clipRule="evenodd"
										/>
									</svg>
								)}
							</button>
						</div>
					)
				})}
			</div>
			<div className="flex justify-between">
				<Button type="button" variant="light" onClick={() => setStep(step - 1)} size="lg">
					Back
				</Button>
				<Button type="button" onClick={() => setStep(step + 1)} size="lg">
					Last Step
				</Button>
			</div>
		</div>,
		<div className="min-h-[300px]">
			<div className="mb-8">
				<TextArea value={formState.comments} rows={5} optional label="Comments" id="comments" onChange={handleChange} />
			</div>
			<div className="flex justify-between">
				<Button type="button" variant="light" onClick={() => setStep(step - 1)} size="lg">
					Back
				</Button>
				<Button type="button" size="lg" onClick={submitForm}>
					Finish & Submit
				</Button>
			</div>
		</div>,
		<div className="min-h-[300px]">
			<div className="flex flex-col items-center gap-y-3">
				<AnimatedCheckmark size="lg" />
				<H3 className="font-body">All set!</H3>
				<P className="max-w-md text-center">We've got your info and have passed it along to your ARC Account Manager.</P>
			</div>
		</div>
	]

	const titleSteps = [
		<H2>Let's start with the basics. Where can we reach you?</H2>,
		<H2>
			Next, where are you from, and what do you do?<span className="mt-1 block text-sm font-medium tracking-wide text-gray-400">(We just need your district)</span>
		</H2>,
		<H2>Finally, what products are you interested in?</H2>,
		<H2>Anything else you'd like us to know?</H2>
	]

	return (
		<Layout>
			<Section flatGray className="relative pb-0">
				<div className="absolute top-0 h-full w-full">
					<div className="rainbow-gradient-light absolute right-0 bottom-0 top-auto h-[250px] w-[250px] rounded-[30%_70%_71%_29%_/_30%_30%_70%_70%] opacity-40 blur-[30px] sm:block md:bottom-auto md:top-8 md:h-[500px] md:w-[500px]"></div>
					<div className="rainbow-gradient-light absolute left-0 right-auto -top-[110px] hidden h-[300px] w-[300px] -translate-x-[50%] translate-y-[20%] rounded-full opacity-40 blur-[30px] sm:block"></div>
				</div>
				{process.env.NODE_ENV === 'development' && (
					<button className="absolute top-0 left-0 z-20" onClick={goToThankYou}>
						Go to Thank You
					</button>
				)}
				<Row center className="py-20">
					<Col width="w-full md:w-3/4" className="relative z-0">
						<span className="absolute -bottom-6 -left-6 z-[-1] hidden sm:inline">
							<svg width="107" height="134" viewBox="0 0 107 134" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="104.999" cy="132" r="1.66667" transform="rotate(180 104.999 132)" fill="#d1d5db" />
								<circle cx="104.999" cy="117.333" r="1.66667" transform="rotate(180 104.999 117.333)" fill="#d1d5db" />
								<circle cx="104.999" cy="102.667" r="1.66667" transform="rotate(180 104.999 102.667)" fill="#d1d5db" />
								<circle cx="104.999" cy="88" r="1.66667" transform="rotate(180 104.999 88)" fill="#d1d5db" />
								<circle cx="104.999" cy="73.3333" r="1.66667" transform="rotate(180 104.999 73.3333)" fill="#d1d5db" />
								<circle cx="104.999" cy="45" r="1.66667" transform="rotate(180 104.999 45)" fill="#d1d5db" />
								<circle cx="104.999" cy="16" r="1.66667" transform="rotate(180 104.999 16)" fill="#d1d5db" />
								<circle cx="104.999" cy="59" r="1.66667" transform="rotate(180 104.999 59)" fill="#d1d5db" />
								<circle cx="104.999" cy="30.6666" r="1.66667" transform="rotate(180 104.999 30.6666)" fill="#d1d5db" />
								<circle cx="104.999" cy="1.66665" r="1.66667" transform="rotate(180 104.999 1.66665)" fill="#d1d5db" />
								<circle cx="90.3333" cy="132" r="1.66667" transform="rotate(180 90.3333 132)" fill="#d1d5db" />
								<circle cx="90.3333" cy="117.333" r="1.66667" transform="rotate(180 90.3333 117.333)" fill="#d1d5db" />
								<circle cx="90.3333" cy="102.667" r="1.66667" transform="rotate(180 90.3333 102.667)" fill="#d1d5db" />
								<circle cx="90.3333" cy="88" r="1.66667" transform="rotate(180 90.3333 88)" fill="#d1d5db" />
								<circle cx="90.3333" cy="73.3333" r="1.66667" transform="rotate(180 90.3333 73.3333)" fill="#d1d5db" />
								<circle cx="90.3333" cy="45" r="1.66667" transform="rotate(180 90.3333 45)" fill="#d1d5db" />
								<circle cx="90.3333" cy="16" r="1.66667" transform="rotate(180 90.3333 16)" fill="#d1d5db" />
								<circle cx="90.3333" cy="59" r="1.66667" transform="rotate(180 90.3333 59)" fill="#d1d5db" />
								<circle cx="90.3333" cy="30.6666" r="1.66667" transform="rotate(180 90.3333 30.6666)" fill="#d1d5db" />
								<circle cx="90.3333" cy="1.66665" r="1.66667" transform="rotate(180 90.3333 1.66665)" fill="#d1d5db" />
								<circle cx="75.6654" cy="132" r="1.66667" transform="rotate(180 75.6654 132)" fill="#d1d5db" />
								<circle cx="31.9993" cy="132" r="1.66667" transform="rotate(180 31.9993 132)" fill="#d1d5db" />
								<circle cx="75.6654" cy="117.333" r="1.66667" transform="rotate(180 75.6654 117.333)" fill="#d1d5db" />
								<circle cx="31.9993" cy="117.333" r="1.66667" transform="rotate(180 31.9993 117.333)" fill="#d1d5db" />
								<circle cx="75.6654" cy="102.667" r="1.66667" transform="rotate(180 75.6654 102.667)" fill="#d1d5db" />
								<circle cx="31.9993" cy="102.667" r="1.66667" transform="rotate(180 31.9993 102.667)" fill="#d1d5db" />
								<circle cx="75.6654" cy="88" r="1.66667" transform="rotate(180 75.6654 88)" fill="#d1d5db" />
								<circle cx="31.9993" cy="88" r="1.66667" transform="rotate(180 31.9993 88)" fill="#d1d5db" />
								<circle cx="75.6654" cy="73.3333" r="1.66667" transform="rotate(180 75.6654 73.3333)" fill="#d1d5db" />
								<circle cx="31.9993" cy="73.3333" r="1.66667" transform="rotate(180 31.9993 73.3333)" fill="#d1d5db" />
								<circle cx="75.6654" cy="45" r="1.66667" transform="rotate(180 75.6654 45)" fill="#d1d5db" />
								<circle cx="31.9993" cy="45" r="1.66667" transform="rotate(180 31.9993 45)" fill="#d1d5db" />
								<circle cx="75.6654" cy="16" r="1.66667" transform="rotate(180 75.6654 16)" fill="#d1d5db" />
								<circle cx="31.9993" cy="16" r="1.66667" transform="rotate(180 31.9993 16)" fill="#d1d5db" />
								<circle cx="75.6654" cy="59" r="1.66667" transform="rotate(180 75.6654 59)" fill="#d1d5db" />
								<circle cx="31.9993" cy="59" r="1.66667" transform="rotate(180 31.9993 59)" fill="#d1d5db" />
								<circle cx="75.6654" cy="30.6666" r="1.66667" transform="rotate(180 75.6654 30.6666)" fill="#d1d5db" />
								<circle cx="31.9993" cy="30.6666" r="1.66667" transform="rotate(180 31.9993 30.6666)" fill="#d1d5db" />
								<circle cx="75.6654" cy="1.66665" r="1.66667" transform="rotate(180 75.6654 1.66665)" fill="#d1d5db" />
								<circle cx="31.9993" cy="1.66665" r="1.66667" transform="rotate(180 31.9993 1.66665)" fill="#d1d5db" />
								<circle cx="60.9993" cy="132" r="1.66667" transform="rotate(180 60.9993 132)" fill="#d1d5db" />
								<circle cx="17.3333" cy="132" r="1.66667" transform="rotate(180 17.3333 132)" fill="#d1d5db" />
								<circle cx="60.9993" cy="117.333" r="1.66667" transform="rotate(180 60.9993 117.333)" fill="#d1d5db" />
								<circle cx="17.3333" cy="117.333" r="1.66667" transform="rotate(180 17.3333 117.333)" fill="#d1d5db" />
								<circle cx="60.9993" cy="102.667" r="1.66667" transform="rotate(180 60.9993 102.667)" fill="#d1d5db" />
								<circle cx="17.3333" cy="102.667" r="1.66667" transform="rotate(180 17.3333 102.667)" fill="#d1d5db" />
								<circle cx="60.9993" cy="88" r="1.66667" transform="rotate(180 60.9993 88)" fill="#d1d5db" />
								<circle cx="17.3333" cy="88" r="1.66667" transform="rotate(180 17.3333 88)" fill="#d1d5db" />
								<circle cx="60.9993" cy="73.3333" r="1.66667" transform="rotate(180 60.9993 73.3333)" fill="#d1d5db" />
								<circle cx="17.3333" cy="73.3333" r="1.66667" transform="rotate(180 17.3333 73.3333)" fill="#d1d5db" />
								<circle cx="60.9993" cy="45" r="1.66667" transform="rotate(180 60.9993 45)" fill="#d1d5db" />
								<circle cx="17.3333" cy="45" r="1.66667" transform="rotate(180 17.3333 45)" fill="#d1d5db" />
								<circle cx="60.9993" cy="16" r="1.66667" transform="rotate(180 60.9993 16)" fill="#d1d5db" />
								<circle cx="17.3333" cy="16" r="1.66667" transform="rotate(180 17.3333 16)" fill="#d1d5db" />
								<circle cx="60.9993" cy="59" r="1.66667" transform="rotate(180 60.9993 59)" fill="#d1d5db" />
								<circle cx="17.3333" cy="59" r="1.66667" transform="rotate(180 17.3333 59)" fill="#d1d5db" />
								<circle cx="60.9993" cy="30.6666" r="1.66667" transform="rotate(180 60.9993 30.6666)" fill="#d1d5db" />
								<circle cx="17.3333" cy="30.6666" r="1.66667" transform="rotate(180 17.3333 30.6666)" fill="#d1d5db" />
								<circle cx="60.9993" cy="1.66665" r="1.66667" transform="rotate(180 60.9993 1.66665)" fill="#d1d5db" />
								<circle cx="17.3333" cy="1.66665" r="1.66667" transform="rotate(180 17.3333 1.66665)" fill="#d1d5db" />
								<circle cx="46.3333" cy="132" r="1.66667" transform="rotate(180 46.3333 132)" fill="#d1d5db" />
								<circle cx="2.66536" cy="132" r="1.66667" transform="rotate(180 2.66536 132)" fill="#d1d5db" />
								<circle cx="46.3333" cy="117.333" r="1.66667" transform="rotate(180 46.3333 117.333)" fill="#d1d5db" />
								<circle cx="2.66536" cy="117.333" r="1.66667" transform="rotate(180 2.66536 117.333)" fill="#d1d5db" />
								<circle cx="46.3333" cy="102.667" r="1.66667" transform="rotate(180 46.3333 102.667)" fill="#d1d5db" />
								<circle cx="2.66536" cy="102.667" r="1.66667" transform="rotate(180 2.66536 102.667)" fill="#d1d5db" />
								<circle cx="46.3333" cy="88" r="1.66667" transform="rotate(180 46.3333 88)" fill="#d1d5db" />
								<circle cx="2.66536" cy="88" r="1.66667" transform="rotate(180 2.66536 88)" fill="#d1d5db" />
								<circle cx="46.3333" cy="73.3333" r="1.66667" transform="rotate(180 46.3333 73.3333)" fill="#d1d5db" />
								<circle cx="2.66536" cy="73.3333" r="1.66667" transform="rotate(180 2.66536 73.3333)" fill="#d1d5db" />
								<circle cx="46.3333" cy="45" r="1.66667" transform="rotate(180 46.3333 45)" fill="#d1d5db" />
								<circle cx="2.66536" cy="45" r="1.66667" transform="rotate(180 2.66536 45)" fill="#d1d5db" />
								<circle cx="46.3333" cy="16" r="1.66667" transform="rotate(180 46.3333 16)" fill="#d1d5db" />
								<circle cx="2.66536" cy="16" r="1.66667" transform="rotate(180 2.66536 16)" fill="#d1d5db" />
								<circle cx="46.3333" cy="59" r="1.66667" transform="rotate(180 46.3333 59)" fill="#d1d5db" />
								<circle cx="2.66536" cy="59" r="1.66667" transform="rotate(180 2.66536 59)" fill="#d1d5db" />
								<circle cx="46.3333" cy="30.6666" r="1.66667" transform="rotate(180 46.3333 30.6666)" fill="#d1d5db" />
								<circle cx="2.66536" cy="30.6666" r="1.66667" transform="rotate(180 2.66536 30.6666)" fill="#d1d5db" />
								<circle cx="46.3333" cy="1.66665" r="1.66667" transform="rotate(180 46.3333 1.66665)" fill="#d1d5db" />
								<circle cx="2.66536" cy="1.66665" r="1.66667" transform="rotate(180 2.66536 1.66665)" fill="#d1d5db" />
							</svg>
						</span>
						<span className="absolute -top-10 -right-6 z-[-1] hidden sm:inline">
							<svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path fillRule="evenodd" clipRule="evenodd" d="M0 100C0 44.7715 0 0 0 0C55.2285 0 100 44.7715 100 100C100 100 100 100 0 100Z" fill="#0271BC"></path>
							</svg>
						</span>
						<span className="absolute top-[5.5rem] -right-6 hidden sm:block">
							<svg width="34" height="134" viewBox="0 0 34 134" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="31.9993" cy="132" r="1.66667" transform="rotate(180 31.9993 132)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="117.333" r="1.66667" transform="rotate(180 31.9993 117.333)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="102.667" r="1.66667" transform="rotate(180 31.9993 102.667)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="88" r="1.66667" transform="rotate(180 31.9993 88)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="73.3333" r="1.66667" transform="rotate(180 31.9993 73.3333)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="45" r="1.66667" transform="rotate(180 31.9993 45)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="16" r="1.66667" transform="rotate(180 31.9993 16)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="59" r="1.66667" transform="rotate(180 31.9993 59)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="30.6666" r="1.66667" transform="rotate(180 31.9993 30.6666)" fill="#d1d5db"></circle>
								<circle cx="31.9993" cy="1.66665" r="1.66667" transform="rotate(180 31.9993 1.66665)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="132" r="1.66667" transform="rotate(180 17.3333 132)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="117.333" r="1.66667" transform="rotate(180 17.3333 117.333)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="102.667" r="1.66667" transform="rotate(180 17.3333 102.667)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="88" r="1.66667" transform="rotate(180 17.3333 88)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="73.3333" r="1.66667" transform="rotate(180 17.3333 73.3333)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="45" r="1.66667" transform="rotate(180 17.3333 45)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="16" r="1.66667" transform="rotate(180 17.3333 16)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="59" r="1.66667" transform="rotate(180 17.3333 59)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="30.6666" r="1.66667" transform="rotate(180 17.3333 30.6666)" fill="#d1d5db"></circle>
								<circle cx="17.3333" cy="1.66665" r="1.66667" transform="rotate(180 17.3333 1.66665)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="132" r="1.66667" transform="rotate(180 2.66536 132)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="117.333" r="1.66667" transform="rotate(180 2.66536 117.333)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="102.667" r="1.66667" transform="rotate(180 2.66536 102.667)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="88" r="1.66667" transform="rotate(180 2.66536 88)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="73.3333" r="1.66667" transform="rotate(180 2.66536 73.3333)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="45" r="1.66667" transform="rotate(180 2.66536 45)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="16" r="1.66667" transform="rotate(180 2.66536 16)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="59" r="1.66667" transform="rotate(180 2.66536 59)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="30.6666" r="1.66667" transform="rotate(180 2.66536 30.6666)" fill="#d1d5db"></circle>
								<circle cx="2.66536" cy="1.66665" r="1.66667" transform="rotate(180 2.66536 1.66665)" fill="#d1d5db"></circle>
							</svg>
						</span>
						<div className="relative overflow-hidden rounded-lg border bg-white shadow-lg">
							<AnimatePresence>
								{loading && (
									<motion.div
										initial={{ opacity: 0 }}
										animate={{ opacity: 1, translateY: 0 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.8 }}
										className="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center bg-gray-800/70"
									>
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className="animate-spin" height={60} width={60}>
											<path
												d="M85.4 17.6c-.7 2.1-1.6 4.1-2.7 6C88.5 30.9 92 40 92 50c0 23.2-18.8 42-42 42S8 73.2 8 50c0-10.1 3.6-19.3 9.5-26.6-1-1.9-1.9-4-2.6-6.1C6.9 25.9 2 37.4 2 50c0 26.5 21.5 48 48 48s48-21.5 48-48c0-12.5-4.8-23.8-12.6-32.4z"
												fill="#E6E6E6"
											/>
										</svg>
									</motion.div>
								)}
							</AnimatePresence>
							<div className="mx-auto mb-6 w-full py-10 px-4 md:w-2/3 md:px-0">
								<div className="mb-8 text-center">
									<AnimatePresence>
										{step <= 3 && (
											<motion.div
												initial={{ opacity: 0, translateY: '150%' }}
												animate={{ opacity: 1, translateY: 0 }}
												exit={{ opacity: 0, translateY: '-150%' }}
												transition={{ duration: 0.8 }}
											>
												<span className="mb-2 block text-base font-bold uppercase text-ab-100">Connect With ARC</span>
												<Stepper step={step} />
											</motion.div>
										)}
									</AnimatePresence>

									<div className="overflow-hidden">
										<AnimatePresence exitBeforeEnter>
											<motion.div key={step} variants={slideVerticalVariants} initial="initial" animate="enter" exit="exit">
												{titleSteps[step]}
											</motion.div>
										</AnimatePresence>
									</div>
								</div>
								<div>
									<FormProvider {...methods}>
										<form action="">
											<AnimatePresence exitBeforeEnter>
												<motion.div key={step} variants={slideHorizontalVariants} initial="initial" animate="enter" exit="exit">
													{steps[step]}
												</motion.div>
											</AnimatePresence>
										</form>
									</FormProvider>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</Section>
		</Layout>
	)
}

const inputValidations = {
	first_name: {
		required: 'First name is required',
		minLength: {
			value: 2,
			message: 'First name must be at least 2 characters'
		}
	},
	last_name: {
		required: 'Last name is required',
		minLength: {
			value: 2,
			message: 'Last name must be at least 2 characters'
		}
	},
	email: {
		required: 'Email is required',
		pattern: {
			value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			message: 'Please enter a valid email address'
		}
	},
	zip_code: {
		required: 'Zip code is required',
		pattern: {
			value: /^\d{5}$/,
			message: 'Zip code must be 5 numbers'
		}
	},
	district: {
		required: 'District is required',
		minLength: {
			value: 2,
			message: 'District must be at least 2 characters'
		}
	}
}
